import { forwardRef } from 'react';
import type { BasePopoverProps, PopoverProps } from '@hotjar/hotsauce';
import { Button, useTippy } from '@hotjar/hotsauce';
import { useTranslation } from '@hotjar/translations';
import { S } from './UpsellPopover.styles';

type PlacementOpts = Exclude<PopoverProps['placement'], 'top' | 'bottom'>;

export interface UpsellPopoverProps extends Pick<PopoverProps, 'title' | 'text' | 'children'> {
  upgradeMessage: string;
  handleUpgradeClick: () => void;
  placement?: PlacementOpts;
  trigger?: BasePopoverProps['trigger'];
}

type ContentProps = Pick<
  UpsellPopoverProps,
  'text' | 'title' | 'upgradeMessage' | 'handleUpgradeClick'
>;

const Content = ({ title, text, upgradeMessage, handleUpgradeClick }: ContentProps) => {
  const { t } = useTranslation('upsell');

  return (
    <S.Container>
      <S.Body>
        <S.Header as="p" textType="subtitle1">
          {title}
        </S.Header>
        <S.Copy as="p" textType="body2">
          {text}
        </S.Copy>
      </S.Body>
      <S.Footer>
        <S.Caption as="p" textType="caption">
          {upgradeMessage}
        </S.Caption>
        <Button icon="crown" kind="primary" size="s" onClick={handleUpgradeClick}>
          {t('toolkit.upsell-popover.upgrade')}
        </Button>
      </S.Footer>
    </S.Container>
  );
};

export const UpsellPopover = forwardRef<HTMLElement, UpsellPopoverProps>(
  (
    {
      text,
      title,
      upgradeMessage,
      handleUpgradeClick,
      children,
      ...tippyProps
    }: UpsellPopoverProps,
    ref,
  ): JSX.Element => {
    const tippyInstance = useTippy();

    return (
      <S.StyledBasedPopoverContainer
        ref={ref}
        onCreate={tippyInstance.set}
        content={
          <Content
            text={text}
            title={title}
            upgradeMessage={upgradeMessage}
            handleUpgradeClick={handleUpgradeClick}
          />
        }
        {...tippyProps}
      >
        {children}
      </S.StyledBasedPopoverContainer>
    );
  },
);
